import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, Globe2 } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { IMAGES } from '@/assets/images';
const Contact: React.FC = () => {
  const {
    t,
    isEn
  } = useLanguage();
  const content = {
    title: {
      jp: 'お問い合わせ',
      en: 'Contact Us'
    },
    subtitle: {
      jp: '事業に関するご質問や提携のご相談は、以下の窓口より承っております。',
      en: 'For business inquiries or partnership opportunities, please reach out to us via the following channels.'
    },
    emailLabel: {
      jp: 'メールアドレス',
      en: 'Email Address'
    },
    locationLabel: {
      jp: '所在地',
      en: 'Location'
    },
    locationValue: {
      jp: '大阪府大阪市天王寺区',
      en: 'Osaka, Japan'
    },
    hoursLabel: {
      jp: '営業時間',
      en: 'Business Hours'
    },
    hoursValue: {
      jp: '平日 09:00 - 18:00 (JST)',
      en: 'Mon - Fri 09:00 - 18:00 (JST)'
    },
    notice: {
      jp: '※通常3営業日以内に担当者より折り返しご連絡いたします。',
      en: '*Our representative will respond within 3 business days.'
    },
    multiculturalTitle: {
      jp: '多言語対応について',
      en: 'Multilingual Support'
    },
    multiculturalDesc: {
      jp: '日本語・英語・中国語でのコミュニケーションが可能です。グローバルなビジネス展開をサポートいたします。',
      en: 'We provide support in Japanese, English, and Chinese to facilitate your global business expansion.'
    }
  };
  return <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.OFFICE_BUILDING_8} alt="MMK Osaka Office" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-bold text-primary mb-4">
            {t(content.title)}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(content.subtitle)}
          </motion.p>
        </div>
      </section>

      {/* Contact Info Grid */}
      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-8">
            <div className="flex items-start gap-6 p-8 bg-card border border-border rounded-lg shadow-sm">
              <div className="p-3 bg-primary/10 rounded-md text-primary">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {t(content.emailLabel)}
                </h3>
                <a href="mailto:mmk221.2025@gmail.com" className="text-xl font-medium text-foreground hover:text-primary transition-colors underline underline-offset-4">
                  mmk221.2025@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-6 p-8 bg-card border border-border rounded-lg shadow-sm">
              <div className="p-3 bg-primary/10 rounded-md text-primary">
                <MapPin size={24} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {t(content.locationLabel)}
                </h3>
                <p className="text-xl font-medium text-foreground">
                  {t(content.locationValue)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6 p-8 bg-card border border-border rounded-lg shadow-sm">
              <div className="p-3 bg-primary/10 rounded-md text-primary">
                <Clock size={24} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {t(content.hoursLabel)}
                </h3>
                <p className="text-xl font-medium text-foreground">
                  {t(content.hoursValue)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-primary text-primary-foreground p-10 rounded-lg flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-6">
              <Globe2 size={32} className="text-accent" />
              <h2 className="text-2xl font-bold">{t(content.multiculturalTitle)}</h2>
            </div>
            <p className="text-lg opacity-90 leading-relaxed mb-8">
              {t(content.multiculturalDesc)}
            </p>
            <div className="border-t border-primary-foreground/20 pt-8">
              <p className="text-sm opacity-70 italic">
                {t(content.notice)}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA / Branding */}
      <section className="py-20 bg-muted/30 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-sm font-bold text-primary tracking-widest uppercase mb-4">
            Corporate Integrity
          </h2>
          <p className="text-muted-foreground font-mono text-sm">
            株式会社M.M.K | M.M.K Co., Ltd.
          </p>
        </div>
      </section>
    </div>;
};
export default Contact;